import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customerDevicesTableId } from '@/config/table-ids';
import { ApiError } from '@/lib/api/errors';

import {
  confirmParamTemplate,
  listOrderDevices,
  listParamTemplates,
  listProductTypes,
  setParamTemplate,
  verifySerial,
} from './api';
import type { OrderScope, SetParamTemplateBody } from './types';

/**
 * Own key root, deliberately separate from `orderKeys`: this is a device concern an order scopes, not
 * an order sub-resource (ADR-0024). Ticket 06's apply invalidates this **and** the customer devices
 * table's own `tableId` root — the order detail is deliberately not a third root; see
 * `useApplyParamTemplate` below.
 */
export const deviceParamsKeys = {
  all: ['device-params'] as const,
  orderDevices: (orderId: string) => [...deviceParamsKeys.all, 'order-devices', orderId] as const,
  productTypes: () => [...deviceParamsKeys.all, 'product-types'] as const,
  paramTemplates: (productName: string) =>
    [...deviceParamsKeys.all, 'param-templates', productName] as const,
};

/**
 * The eligible Units for the modal's device source. The source is **parameterised by scope** rather
 * than forked per host (ADR-0024); ticket 09 adds the customer branch here, leaving every call site
 * untouched.
 *
 * Enabled unconditionally. Gating the fetch on order status would itself be frontend eligibility
 * logic — the thing AC8 forbids — so the backend is asked every time and answers `[]` when there is
 * nothing to configure.
 */
export function useEligibleDevices(
  scope: OrderScope,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: deviceParamsKeys.orderDevices(scope.orderId),
    queryFn: () => listOrderDevices(scope.orderId),
    enabled: enabled && Boolean(scope.orderId),
  });
}

/**
 * Verifies one serial against Digital Matter. A **mutation, not a query**, deliberately: it must fire
 * only on Step 1's Next (AC9), and a query keyed on the serial would re-issue it on every keystroke.
 * Nothing is invalidated — this is a read-only check that writes no server state.
 */
export function useVerifySerial() {
  return useMutation({ mutationFn: verifySerial });
}

/** The DM Category list. One global list, so no scope in the key. */
export function useProductTypes() {
  return useQuery({ queryKey: deviceParamsKeys.productTypes(), queryFn: listProductTypes });
}

/**
 * The templates for one DM Category. **Keyed on the category**, so changing it refetches rather than
 * filtering client-side (AC4) — and a category the admin has already visited comes back from cache.
 *
 * `staleTime: 0` opts out of the app's 30-second default, and it is **load-bearing for AC6**: the modal
 * is `destroyOnHidden`, so reopening the flow remounts this query — but with a 30s stale window a reopen
 * inside that window would serve the cached list and a template just created in Digital Matter would not
 * appear. The list is tiny and the admin opens this modal deliberately, so the extra request is free.
 */
export function useParamTemplates(productName: string) {
  return useQuery({
    queryKey: deviceParamsKeys.paramTemplates(productName),
    queryFn: () => listParamTemplates(productName),
    enabled: productName !== '',
    staleTime: 0,
  });
}

/**
 * The already-configured conflict. **HTTP 409 is an assumption, not a contract** — BE has been asked
 * for the real signal (FLAG-BE-4) — so it is decided in exactly one place, and both the cache refresh
 * below and the modal's toast branch read it from here. When BE answers, this function changes and
 * nothing else does.
 *
 * Keyed on the **server's response**, never on a local "already applied" flag: Back from Preview lets
 * the same admin apply a second time and AC7 requires that to succeed, while a *different* admin's
 * configuration must still block. Only the server can tell those two apart (FLAG-BE-8).
 */
export const isAlreadyConfigured = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status === 409;

/**
 * Applies the template, then refreshes the two roots the affordance and the device status are read
 * from: **this feature's key factory** — the eligible-units query behind the ellipsis item, ticket
 * 08's row link and Step 1's dropdown — and the **Customer Devices `<DataTable>`'s own `tableId`
 * root**. They are different keys and missing either leaves a stale affordance (AC9, grilling Q15.3).
 *
 * **The apply is two calls under one mutation** (ticket 13): `set` writes the template and leaves the
 * device in Pending Confirmation, `confirm` moves it to Pending QC. They are chained here rather than
 * in the component so one press produces one loading state and one outcome, with no intermediate UI —
 * the admin never learns there are two (AC3).
 *
 * **A failed confirmation resolves rather than throws**, because it is not a failed apply: the
 * template is on the device and only the last step is missing, and the backend deliberately keeps a
 * `pending_confirmation` Unit **eligible** (greenway_api#212) so re-running the wizard finishes the
 * job. Throwing would route it into the error branch, where `confirm`'s own 409
 * (`NOT_PENDING_CONFIRMATION`) is indistinguishable **by status** from the set's already-configured
 * 409 — the admin would be told another admin got there first about a device they just configured
 * themselves, and the flow would close for good. The outcome is a value for exactly that reason.
 *
 * The order detail is deliberately *not* invalidated: it carries nothing this action changes, and
 * importing `orderKeys` here would close the `orders ↔ device-params` cycle ADR-0024 hoisted the Unit
 * codec to `lib` to avoid.
 *
 * `onSettled`, not `onSuccess`: a 409 means the server state changed too — another admin configured
 * the device — so the eligible list is just as stale as after our own apply, which is what makes the
 * device disappear from the dropdown (AC5). A 500 or a dropped connection changed nothing, and AC4
 * requires the affordance to stay exactly where it was.
 *
 * A failed confirmation arrives here as **data, not an error**, so it invalidates exactly as a success
 * does — which is correct and load-bearing: the server state did change (the customer-device row now
 * exists in Pending Confirmation), and refetching is what leaves the affordance correctly in place
 * rather than stale (AC7).
 */
export function useApplyParamTemplate(scope: OrderScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetParamTemplateBody) => {
      const { customerDeviceId } = await setParamTemplate(body);
      try {
        await confirmParamTemplate(customerDeviceId);
        return { confirmed: true };
      } catch (error) {
        // Swallowed for the admin, never for us. This is the one path where a real server error is
        // deliberately downgraded to a softer message, so without a signal here nobody would learn
        // that confirmation is failing in production — the drift class this whole fix exists to close.
        Sentry.captureException(error, {
          tags: { param_template_confirm_failed: customerDeviceId },
        });
        return { confirmed: false };
      }
    },
    onSettled: (_data, error) => {
      if (error && !isAlreadyConfigured(error)) return;
      void qc.invalidateQueries({ queryKey: deviceParamsKeys.all });
      void qc.invalidateQueries({ queryKey: [customerDevicesTableId(scope.customerId)] });
    },
  });
}
